import { GatedRollout } from 'common/gated-rollout/GatedRollout';
import { isEqual } from 'lodash';
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import { ShefStatus } from 'src/gqlReactTypings.generated.d';
import { getAppVersion } from 'src/shared/utils/AppUtils';
import { buildLogRocketTraits, redactHeaders, sanitizeUrl } from 'src/tracking/logRocketSanitization';
import { UserInfo } from 'src/tracking/types';

const LOGROCKET_ENABLED = process.env.REACT_APP_LOGROCKET_ENABLED === '1';
const LOGROCKET_APP_ID = process.env.REACT_APP_LOGROCKET_APP_ID;
const ANONYMOUS_SESSION_SAMPLE_PERCENT = 40;
const LOGROCKET_INTERACTION_TIMEOUT_MS = 10_000;
const LOGROCKET_INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll'] as const;

const anonymousSessionRollout = new GatedRollout();

let initialized = false;
let latestSessionUrl: string | undefined;
const sessionUrlListeners = new Set<(sessionUrl: string) => void>();
let lastIdentity: LogRocketIdentity | undefined;
let experimentTraits: Record<string, string> = {};

export interface LogRocketIdentity {
  id: string;
  browserTrackerId: string;
  isAdmin: boolean;
  isShef: boolean;
}

function isAutomatedBrowser(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return navigator.webdriver || /HeadlessChrome/.test(navigator.userAgent);
}

/**
 * Logged-in sessions record at 100%; anonymous ones are sampled on the persistent
 * browserTrackerId rather than per page load, so a visitor's replay history is
 * either complete or absent instead of half a funnel.
 */
function shouldRecordSession(browserTrackerId: string): boolean {
  return (
    lastIdentity !== undefined ||
    anonymousSessionRollout.inRollout(`logrocket_${browserTrackerId}`, ANONYMOUS_SESSION_SAMPLE_PERCENT)
  );
}

export function initLogRocket(browserTrackerId: string): void {
  if (initialized || !LOGROCKET_ENABLED || !LOGROCKET_APP_ID) {
    return;
  }
  if (isAutomatedBrowser() || !shouldRecordSession(browserTrackerId)) {
    return;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const rootHostname = hostname === 'shef.com' || hostname.endsWith('.shef.com') ? '.shef.com' : undefined;

  LogRocket.init(LOGROCKET_APP_ID, {
    release: getAppVersion(),
    rootHostname,
    // Persist identity across shef.com subdomains within a session (pairs with rootHostname).
    persistUserIdInfo: true,
    dom: {
      inputSanitizer: true,
    },
    browser: {
      urlSanitizer: sanitizeUrl,
    },
    network: {
      // Drop request/response bodies, matching Datadog RUM (no body PII to redact).
      requestSanitizer: (request) => ({
        ...request,
        headers: redactHeaders(request.headers),
        body: undefined,
      }),
      responseSanitizer: (response) => ({
        ...response,
        headers: redactHeaders(response.headers),
        body: undefined,
      }),
    },
  });
  setupLogRocketReact();

  LogRocket.getSessionURL((sessionUrl) => {
    latestSessionUrl = sessionUrl;
    sessionUrlListeners.forEach((listener) => listener(sessionUrl));
  });

  initialized = true;
  pushIdentity();
}

/**
 * Records anonymous sessions only when the visitor engages. Recording starts on the first
 * interaction; if none arrives before the timeout, the session never records, so dead bounces
 * and non-interacting bots don't spend replay quota. Logged-in users skip this gate and record
 * at 100% via identifyLogRocket.
 *
 * LogRocket has no retroactive buffer, so recording begins at the triggering interaction and
 * misses everything before it.
 */
export function armLogRocketOnInteraction(browserTrackerId: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const listenerOptions = { capture: true, passive: true } as const;
  let settled = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const handleInteraction = (): void => {
    if (settled) {
      return;
    }
    settled = true;
    clearTimeout(timeoutId);
    LOGROCKET_INTERACTION_EVENTS.forEach((event) =>
      window.removeEventListener(event, handleInteraction, listenerOptions)
    );
    try {
      initLogRocket(browserTrackerId);
    } catch (error) {
      console.error('LogRocket init failed', error);
    }
  };

  timeoutId = setTimeout(() => {
    settled = true;
    LOGROCKET_INTERACTION_EVENTS.forEach((event) =>
      window.removeEventListener(event, handleInteraction, listenerOptions)
    );
  }, LOGROCKET_INTERACTION_TIMEOUT_MS);

  LOGROCKET_INTERACTION_EVENTS.forEach((event) => window.addEventListener(event, handleInteraction, listenerOptions));
}

function pushIdentity(): void {
  if (!initialized) {
    return;
  }
  if (lastIdentity) {
    LogRocket.identify(lastIdentity.id, { ...buildLogRocketTraits(lastIdentity), ...experimentTraits });
  } else if (Object.keys(experimentTraits).length > 0) {
    LogRocket.identify(experimentTraits);
  }
}

export function identifyLogRocket(identity: LogRocketIdentity): void {
  lastIdentity = identity;
  if (!initialized) {
    initLogRocket(identity.browserTrackerId);
    return;
  }
  pushIdentity();
}

/**
 * Syncs experiment states as `exp.<name>` user traits. Callers pass only active
 * splits (rollout > 0, no variant at 100) — unlike Datadog's global context,
 * LogRocket traits have a payload size cap, so concluded/dormant experiments are
 * trimmed upstream. Re-identifies the session only when the map actually changes,
 * so each experiment state transition is captured without identify spam.
 */
export function setLogRocketExperiments(experimentStatuses: Record<string, string>): void {
  const nextTraits = Object.fromEntries(
    Object.entries(experimentStatuses).map(([name, variantName]) => [`exp.${name}`, variantName])
  );
  if (isEqual(nextTraits, experimentTraits)) {
    return;
  }
  experimentTraits = nextTraits;
  pushIdentity();
}

export function identifyLogRocketFromUserInfo(userInfo: UserInfo, browserTrackerId: string): void {
  identifyLogRocket({
    id: userInfo.id,
    browserTrackerId,
    isAdmin: Boolean(userInfo.admin),
    isShef: (userInfo.shefStatus ?? ShefStatus.None) !== ShefStatus.None,
  });
}

export function logRocketStartNewSession(): void {
  lastIdentity = undefined;
  // LogRocket merges identify traits and never removes keys, so carrying the map across
  // the boundary would pin the old user's exp.* traits (and dedupe) onto the new session.
  // The reporter effect re-syncs right after: logout flips its userId dependency.
  experimentTraits = {};
  if (!initialized) {
    return;
  }
  LogRocket.startNewSession();
}

export function getLogRocketSessionUrl(): string | undefined {
  return latestSessionUrl;
}

export function onLogRocketSessionUrl(listener: (sessionUrl: string) => void): void {
  sessionUrlListeners.add(listener);
  if (latestSessionUrl !== undefined) {
    listener(latestSessionUrl);
  }
}
